
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Container, Typography, Grid, Card, CardContent, Button, Box,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, Avatar, IconButton, Tooltip, LinearProgress, Stack
} from '@mui/material';
import {
  Add, Inventory2, ShoppingCart, TrendingUp, Visibility,
  CloudUpload, Assessment, LocalShipping, Star, ArrowForward
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { inventoryAPI, transactionAPI } from '../services/api';

const MotionCard = motion(Card);

const DashboardPage = () => {
  const { user } = useAuth();
  const [myItems, setMyItems] = useState([]);
  const [sales, setSales] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [itemsRes, salesRes, purchasesRes] = await Promise.all([
        inventoryAPI.getMyItems(),
        transactionAPI.mySales(),
        transactionAPI.myPurchases(),
      ]);
      setMyItems(itemsRes.data);
      setSales(salesRes.data);
      setPurchases(purchasesRes.data);
    } catch (err) {
      console.error('Dashboard load failed', err);
    } finally {
      setLoading(false);
    }
  };

  const activeItems = myItems.filter(i => i.status === 'active');
  const soldItems = myItems.filter(i => i.status === 'sold');
  const totalRevenue = sales
    .filter(t => t.status === 'completed' || t.status === 'delivered')
    .reduce((sum, t) => sum + (t.final_price || t.offered_price), 0);
  const totalViews = myItems.reduce((sum, i) => sum + (i.views_count || 0), 0);
  const pendingOrders = sales.filter(t => t.status === 'pending').length;

  const statCards = [
    { label: 'Active Listings', value: activeItems.length, icon: <Inventory2 />, gradient: 'linear-gradient(135deg, #2563eb, #3b82f6)', shadow: 'rgba(37,99,235,0.3)' },
    { label: 'Items Sold', value: soldItems.length, icon: <ShoppingCart />, gradient: 'linear-gradient(135deg, #059669, #10b981)', shadow: 'rgba(5,150,105,0.3)' },
    { label: 'Total Revenue', value: `₹${totalRevenue.toLocaleString()}`, icon: <TrendingUp />, gradient: 'linear-gradient(135deg, #7c3aed, #8b5cf6)', shadow: 'rgba(124,58,237,0.3)' },
    { label: 'Total Views', value: totalViews, icon: <Visibility />, gradient: 'linear-gradient(135deg, #d97706, #f59e0b)', shadow: 'rgba(217,119,6,0.3)' },
  ];

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '80vh' }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box>
            <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: '-0.02em' }}>Dashboard</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Welcome back, {user?.full_name || 'User'} 👋
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5}>
            {pendingOrders > 0 && (
              <Button variant="outlined" color="warning" component={Link} to="/orders" size="small"
                startIcon={<LocalShipping />}>
                {pendingOrders} Pending
              </Button>
            )}
            <Button variant="contained" startIcon={<CloudUpload />} component={Link} to="/upload" size="medium">
              Upload Item
            </Button>
          </Stack>
        </Box>

        {loading && <LinearProgress sx={{ mb: 3, borderRadius: 2 }} />}

        {/* Stat Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {statCards.map((stat, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <MotionCard
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                sx={{ borderRadius: 3, overflow: 'visible', border: 'none' }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5 }}>
                        {stat.label}
                      </Typography>
                      <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: '-0.02em' }}>
                        {stat.value}
                      </Typography>
                    </Box>
                    <Avatar sx={{
                      background: stat.gradient, width: 52, height: 52,
                      boxShadow: `0 8px 20px ${stat.shadow}`,
                    }}>
                      {stat.icon}
                    </Avatar>
                  </Box>
                </CardContent>
              </MotionCard>
            </Grid>
          ))}
        </Grid>

        {/* Recent Orders Quick View */}
        {(sales.length > 0 || purchases.length > 0) && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 3 }}>
                <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" fontWeight={700}>Recent Sales</Typography>
                  <Button size="small" component={Link} to="/orders" endIcon={<ArrowForward />}>View All</Button>
                </Box>
                {sales.slice(0, 3).map(s => (
                  <Box key={s.id} sx={{ px: 3, py: 1.5, borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{s.order_number}</Typography>
                      <Typography variant="caption" color="text.secondary">₹{s.offered_price?.toLocaleString()}</Typography>
                    </Box>
                    <Chip label={s.status} size="small"
                      color={s.status === 'pending' ? 'warning' : s.status === 'delivered' ? 'success' : 'default'} />
                  </Box>
                ))}
                {sales.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ px: 3, pb: 3 }}>No sales yet</Typography>
                )}
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 3 }}>
                <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" fontWeight={700}>My Purchases</Typography>
                  <Button size="small" component={Link} to="/orders" endIcon={<ArrowForward />}>View All</Button>
                </Box>
                {purchases.slice(0, 3).map(p => (
                  <Box key={p.id} sx={{ px: 3, py: 1.5, borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{p.order_number}</Typography>
                      <Typography variant="caption" color="text.secondary">₹{p.offered_price?.toLocaleString()}</Typography>
                    </Box>
                    <Chip label={p.status} size="small"
                      color={p.status === 'shipped' ? 'info' : p.status === 'delivered' ? 'success' : 'default'} />
                  </Box>
                ))}
                {purchases.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ px: 3, pb: 3 }}>No purchases yet</Typography>
                )}
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Inventory Table */}
        <Card sx={{ borderRadius: 3 }}>
          <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar sx={{ bgcolor: '#eff6ff', color: '#2563eb', width: 36, height: 36 }}>
                <Assessment sx={{ fontSize: 20 }} />
              </Avatar>
              <Typography variant="h6" fontWeight={700}>My Inventory</Typography>
            </Box>
            <Chip label={`${myItems.length} items`} size="small" variant="outlined" />
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem' }}>Item Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem' }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem' }}>Qty</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem' }}>Price</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem' }}>Views</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem' }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {myItems.slice(0, 10).map((item) => (
                  <TableRow
                    key={item.id} hover
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f8fafc' }, textDecoration: 'none' }}
                    component={Link} to={`/item/${item.id}`}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{item.item_name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={item.category} size="small" variant="outlined" sx={{ borderRadius: 1.5 }} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{item.quantity}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={700} color="primary.main" variant="body2">
                        ₹{item.selling_price?.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Visibility sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography variant="body2">{item.views_count || 0}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.status}
                        size="small"
                        color={item.status === 'active' ? 'success' : item.status === 'sold' ? 'default' : 'warning'}
                        sx={{ borderRadius: 1.5, fontWeight: 600, fontSize: '0.7rem' }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {myItems.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                      <Inventory2 sx={{ fontSize: 52, color: '#e2e8f0', mb: 2 }} />
                      <Typography color="text.secondary" gutterBottom fontWeight={500}>
                        No inventory items yet
                      </Typography>
                      <Typography variant="body2" color="text.disabled" sx={{ mb: 2 }}>
                        Start selling by uploading your first surplus item
                      </Typography>
                      <Button variant="contained" component={Link} to="/upload" startIcon={<Add />} size="small">
                        Upload First Item
                      </Button>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Container>
    </Box>
  );
};

export default DashboardPage;
